// engine/scene/Scene.ts
import { GameObject } from '../GameObject';
import { Component } from '../Component';
import { System } from '../systems/System';

export type UpdateStep = (deltaTime: number) => void;

export type SystemPhase = 'pre' | 'main' | 'post';

type SystemEntry = {
  name: string;
  system: System;
  phase: SystemPhase;
  priority: number;
  onResize?: () => void;
};

export abstract class Scene {
  readonly gameObjects: GameObject[] = [];
  readonly preUpdateSteps: UpdateStep[] = [];
  readonly postUpdateSteps: UpdateStep[] = [];
  protected managers: any = null; // Менеджеры игры

  // System Host - управление системами сцены
  private readonly systems = {
    pre: [] as SystemEntry[],
    main: [] as SystemEntry[],
    post: [] as SystemEntry[],
  };

  // Инициализация сцены с менеджерами
  initialize(managers: any) {
    this.managers = managers;
  }

  add(gameObject: GameObject) {
    this.gameObjects.push(gameObject);
    gameObject.scene = this;
    gameObject._onAddedToScene(this);
    return gameObject;
  }

  remove(gameObject: GameObject) {
    const index = this.gameObjects.indexOf(gameObject);
    if (index >= 0) {
      this.gameObjects[index] = this.gameObjects[this.gameObjects.length - 1];
      this.gameObjects.pop();
      gameObject.scene = null;
      gameObject._onRemovedFromScene();
    }
  }

  onEnter(_previousScene: Scene | null): void {}
  onExit(_nextScene: Scene | null): void {}
  onSuspend(): void {}
  onResume(): void {}

  update(deltaTime: number) {
    for (const step of this.preUpdateSteps) step(deltaTime);
    for (const s of this.systems.pre) s.system.update(deltaTime);

    for (const gameObject of this.gameObjects) gameObject.update(deltaTime);

    for (const s of this.systems.main) s.system.update(deltaTime);
    for (const s of this.systems.post) s.system.update(deltaTime);
    for (const step of this.postUpdateSteps) step(deltaTime);
  }

  /**
   * Найти все GameObject'ы с определенным компонентом
   */
  findGameObjectsWithComponent<T extends Component>(
    componentType: new (...args: any[]) => T
  ): GameObject[] {
    return this.gameObjects.filter(gameObject => gameObject.has(componentType));
  }

  /**
   * Найти все GameObject'ы с несколькими компонентами
   */
  findGameObjectsWithComponents<T extends Component>(
    ...componentTypes: (new (...args: any[]) => T)[]
  ): GameObject[] {
    return this.gameObjects.filter(gameObject =>
      componentTypes.every(componentType => gameObject.has(componentType))
    );
  }

  /**
   * Найти GameObject'ы в радиусе от точки
   */
  findGameObjectsInRadius<T extends Component>(
    centerX: number,
    centerY: number,
    radius: number,
    componentType?: new (...args: any[]) => T
  ): GameObject[] {
    let candidates = componentType
      ? this.findGameObjectsWithComponent(componentType)
      : this.gameObjects;

    return candidates.filter(gameObject => {
      const dx = gameObject.x - centerX;
      const dy = gameObject.y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance <= radius;
    });
  }

  /**
   * Найти ближайший GameObject с компонентом
   */
  findNearestGameObjectWithComponent<T extends Component>(
    centerX: number,
    centerY: number,
    componentType: new (...args: any[]) => T
  ): GameObject | null {
    const candidates = this.findGameObjectsWithComponent(componentType);

    if (candidates.length === 0) return null;

    let nearest = candidates[0];
    let nearestDistance = Math.sqrt(
      Math.pow(nearest.x - centerX, 2) + Math.pow(nearest.y - centerY, 2)
    );

    for (let i = 1; i < candidates.length; i++) {
      const gameObject = candidates[i];
      const distance = Math.sqrt(
        Math.pow(gameObject.x - centerX, 2) + Math.pow(gameObject.y - centerY, 2)
      );

      if (distance < nearestDistance) {
        nearest = gameObject;
        nearestDistance = distance;
      }
    }

    return nearest;
  }

  /** ————— System Host API ————— */

  /**
   * Регистрация системы в сцене
   */
  useSystem<T extends System>(
    system: T,
    opts?: {
      name?: string;
      phase?: SystemPhase;
      priority?: number;
      autostart?: boolean;
      onResize?: () => void;
    }
  ): T {
    const phase = opts?.phase ?? 'main';
    const entry: SystemEntry = {
      name: opts?.name ?? system.constructor.name,
      system,
      phase,
      priority: opts?.priority ?? 0,
      onResize: opts?.onResize,
    };
    this.systems[phase].push(entry);
    this.systems[phase].sort(this.sortByPriority);
    if (opts?.autostart) system.start();
    return system;
  }

  /**
   * Удаление системы по имени
   */
  removeSystem(name: string): boolean {
    for (const phase of ['pre', 'main', 'post'] as const) {
      const arr = this.systems[phase];
      const idx = arr.findIndex(s => s.name === name);
      if (idx >= 0) {
        arr[idx].system.stop();
        arr.splice(idx, 1);
        return true;
      }
    }
    return false;
  }

  /**
   * Получение системы по имени
   */
  getSystemByName<T extends System = System>(name: string): T | undefined {
    for (const phase of ['pre', 'main', 'post'] as const) {
      const found = this.systems[phase].find(s => s.name === name);
      if (found) return found.system as T;
    }
    return undefined;
  }

  /**
   * Остановка всех систем
   */
  stopAllSystems(): void {
    for (const phase of ['pre', 'main', 'post'] as const) {
      for (const s of this.systems[phase]) s.system.stop();
      this.systems[phase].length = 0;
    }
  }

  /**
   * Уведомление всех систем о ресайзе
   */
  notifyResize(): void {
    for (const phase of ['pre', 'main', 'post'] as const) {
      for (const s of this.systems[phase]) s.onResize?.();
    }
  }

  /**
   * Сортировка систем по приоритету (высший приоритет первым)
   */
  private sortByPriority(a: SystemEntry, b: SystemEntry): number {
    return (b.priority ?? 0) - (a.priority ?? 0);
  }

  // ── HMR Support ────────────────────────────────────────────────────────────

  /**
   * Get scene state for HMR preservation
   * Override in derived scenes to save custom state
   */
  public getHMRState(): Record<string, any> {
    return {};
  }

  /**
   * Restore scene state after HMR reload
   * Override in derived scenes to restore custom state
   */
  public restoreHMRState(_state: Record<string, any>): void {
    // Override in derived scenes
  }
}
